import { useQuery } from '@tanstack/react-query';
import { getAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { InquiryStatsDtoIn } from '@/models/in/InquiryStatsDtoIn';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import { INQUIRIES_FEATURE_KEY } from '@/hooks/useInquiriesFeature';

/**
 * The office's view of the inquiry queue: how many are its turn to act on, and when a
 * coordinator last wrote.
 *
 * ⚠️ The count is New plus WaitingForOffice, and it replaces unread InquiryNotifications as
 * the office's number. Those are created only by Mention, InternalNote and NewAssignment — all
 * raised by the office about itself — so a coordinator's inquiry created none and the badge
 * sat at 0 however many were waiting. On mercaz-hatzdaka it read 0 with 269 unanswered.
 *
 * ⚠️ `lastUserMessageAt` is not decoration: the count alone cannot tell the office that
 * something arrived. A coordinator replying to an inquiry that is still New leaves it New, so
 * every count is unchanged and a count-watching alert stays silent while a message waits. Two
 * changes cancelling out — one answered as another arrives — hide it the same way. The
 * timestamp moves for every new coordinator message and for nothing else.
 *
 * Both readers — the sidebar badge and the alert — call this hook, and react-query serves them
 * from one cache entry, so the queue is polled once no matter how many mount.
 */

export const INQUIRY_QUEUE_QUERY_KEY = 'management/inquiries/stats?scope=All';

/** 15s rather than the 30 used elsewhere: this drives a chime, and a late chime is not an alert. */
const POLL_INTERVAL = 15000;

interface InquiryQueue {
  /** undefined until the first response — callers must not treat that as zero. */
  count: number | undefined;
  /** undefined until the first response; null when no coordinator has ever written. */
  lastUserMessageAt: string | null | undefined;
  canAccess: boolean;
}

export default function useInquiryQueueCount(): InquiryQueue {
  const { isCurrentUserAdmin, isCurrentUserDebugger, currentUser } = useAuthContext();
  /*
   * The feature flag is folded into canAccess rather than checked separately at each reader,
   * which makes this the single choke point for the office side: with inquiries off, the poll
   * never starts, the badge has no number and the chime has nothing to fire on. A reader that
   * only skipped rendering would leave a screen polling every 15 seconds for a board nobody
   * can open.
   *
   * The flag itself, not useInquiriesFeature: that one also asks the coordinator's permission
   * worlds in the fund the address names, which the office has none of - and InquiryQueueAlert
   * mounts this outside the router, where the address can't be read at all.
   */
  const isFeatureOn = useFeature(INQUIRIES_FEATURE_KEY);
  const canAccess = isFeatureOn
    && (isCurrentUserAdmin() || isCurrentUserDebugger())
    && !!currentUser?.token;
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const { data: stats } = useQuery({
    queryKey: [INQUIRY_QUEUE_QUERY_KEY],
    queryFn: ({ signal }) => getAuth<InquiryStatsDtoIn>(INQUIRY_QUEUE_QUERY_KEY, headers, undefined, signal),
    enabled: canAccess,
    staleTime: 0,
    refetchInterval: POLL_INTERVAL,
    /*
     * ⚠️ החלק הקריטי, וזה מה שהשתיק את הצליל. react-query **עוצר** רענון
     * במרווחים כשהחלון לא במיקוד - זו ברירת המחדל שלו. כלומר בדיוק במצב שבו
     * ההתראה נחוצה - המזכירה עובדת בחלון אחר - הדף הפסיק לשאול את השרת, ואז
     * ברגע שחזרה למיקוד הכול נדגם בבת אחת וכל ההתראות קפצו יחד באיחור.
     *
     * זו הסיבה שהצליל "עבד" רק כשעברת בין חלונות: לא זיהינו כלום עד שחזר
     * המיקוד. התראה שמפסיקה לעבוד כשלא מסתכלים עליה היא לא התראה.
     */
    refetchIntervalInBackground: true,
    /*
     * ⚠️ Overrides the project-wide refetchOnWindowFocus: false, for this query only. Chrome
     * throttles a hidden tab's timers to about once a minute and freezes long-hidden ones, so
     * the interval above cannot be relied on in a background window; without this, coming back
     * to a window waited for the next tick instead of catching up at once.
     */
    refetchOnWindowFocus: true,
  });

  /*
   * WaitingForOffice as well as New: a coordinator answering an inquiry the office had already
   * replied to moves it there, and that is just as much someone's turn to act.
   */
  return {
    count: stats ? stats.new + stats.waitingForOffice : undefined,
    /*
     * ?? null, not the raw value. undefined means "nothing loaded yet" and holds the alert
     * back; an installation whose API predates this field would otherwise send undefined
     * forever and silently disable the whole alert, count included. Absent means "no
     * timestamp signal here", which is null.
     */
    lastUserMessageAt: stats ? (stats.lastUserMessageAt ?? null) : undefined,
    canAccess,
  };
}
